<p *ngIf="loading() && !status() && !failed()" class="m-0 py-8 text-center opacity-75">Loading status...</p>

<!-- A failed call is "unavailable", never "not set up": the field may well be set up. -->
<div *ngIf="failed()" data-status-failed class="flex flex-col items-start gap-1 rounded-lg border border-[var(--app-border)] p-4">
  <span>Status unavailable. It could not be loaded.</span>
  <button type="button" (click)="retry.emit()" class="min-h-11 font-semibold text-[var(--primary-dark)] hover:underline">Try again</button>
</div>

<div *ngIf="status() as s" class="flex flex-col gap-4">
  <div role="status" data-headline
       class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border px-3.5 py-3 font-medium"
       [ngClass]="pillClasses(overall())">
    <span>{{ headline() }}</span>
    <span class="font-normal">Checked {{ s.checkedAt | drawerTime }}</span>
  </div>

  <!-- FMS -->
  <section data-card="fms" class="flex flex-col gap-3 rounded-lg border border-[var(--app-border)] p-4">
    <div class="flex items-center justify-between gap-3">
      <div role="heading" aria-level="3" class="flex items-center gap-2 text-[15px] font-semibold text-[var(--primary-dark)]">
        <span class="material-icons text-[20px]!" aria-hidden="true">storage</span>FMS (Crest)
      </div>
      <app-integration-pill [status]="s.fms.status" [help]="true"></app-integration-pill>
    </div>
    <p class="m-0 leading-normal" [ngClass]="reasonClasses(s.fms.status)">{{ s.fms.reason | drawerTime:'text' }}</p>
    <dl class="m-0 grid grid-cols-[112px_minmax(0,1fr)] gap-x-3 gap-y-2">
      <dt class="opacity-75">Linked to</dt>
      <dd class="m-0 break-words">{{ fmsLink() || 'No FMS block' }}</dd>
      <ng-container *ngIf="s.fms.pull as pull">
        <dt class="opacity-75">Last good pull</dt>
        <dd class="m-0">{{ pull.lastSuccessAt ? (pull.lastSuccessAt | drawerTime) : 'None recorded' }}</dd>
        <dt class="opacity-75">Last attempt</dt>
        <dd class="m-0">{{ pull.lastAttemptAt ? (pull.lastAttemptAt | drawerTime) : 'None recorded' }}<strong *ngIf="pull.lastAttemptFailed"> · failed</strong></dd>
      </ng-container>
      <ng-container *ngIf="s.fms.blockNo">
        <dt class="opacity-75">Newest spray</dt>
        <dd class="m-0">{{ s.fms.newestSpray ? (s.fms.newestSpray | date:DAY) : 'None' }}</dd>
        <dt class="opacity-75">Newest fertiliser</dt>
        <dd class="m-0">{{ s.fms.newestFert ? (s.fms.newestFert | date:DAY) : 'None' }}</dd>
      </ng-container>
    </dl>
    <p *ngIf="s.fms.otherTenant" data-warning="fms" class="m-0 rounded-md bg-[var(--notification-warning)]/15 px-3 py-2">
      This block is in the FMS of {{ s.fms.sourceLabel || s.fms.source }}, not this field's client. Check the link.
    </p>
    <ng-container *ngTemplateOutlet="editAction; context: { tab: 'fms', label: s.fms.blockNo ? 'Edit FMS link' : 'Link an FMS block' }"></ng-container>
  </section>

  <!-- iGator -->
  <section data-card="igator" class="flex flex-col gap-3 rounded-lg border border-[var(--app-border)] p-4">
    <div class="flex items-center justify-between gap-3">
      <div role="heading" aria-level="3" class="flex items-center gap-2 text-[15px] font-semibold text-[var(--primary-dark)]">
        <span class="material-icons text-[20px]!" aria-hidden="true">water_drop</span>iGator irrigation
      </div>
      <app-integration-pill [status]="s.igator.status" [help]="true"></app-integration-pill>
    </div>
    <p class="m-0 leading-normal" [ngClass]="reasonClasses(s.igator.status)">{{ s.igator.reason | drawerTime:'text' }}</p>
    <dl class="m-0 grid grid-cols-[112px_minmax(0,1fr)] gap-x-3 gap-y-2">
      <dt class="opacity-75">Taps</dt>
      <dd class="m-0">
        <span *ngIf="taps().length === 0">No taps linked</span>
        <ul *ngIf="taps().length > 0" class="m-0 flex list-none flex-col gap-1.5 p-0">
          <li *ngFor="let t of taps()" class="flex flex-col">
            <span class="break-words font-medium">{{ t.tap }}</span>
            <span class="text-xs opacity-75">
              <ng-container *ngIf="t.how">{{ t.how }} · </ng-container>
              <ng-container *ngIf="t.system">last run {{ t.lastRunAt | drawerTime }}</ng-container>
              <ng-container *ngIf="!t.system">no runs yet</ng-container>
            </span>
          </li>
        </ul>
      </dd>
      <ng-container *ngFor="let sys of s.igator.systems">
        <dt class="opacity-75">System</dt>
        <dd class="m-0 flex flex-col gap-1">
          <span class="flex flex-wrap items-center gap-2">
            <span class="break-words font-medium">{{ sys.storedName }}</span>
            <span *ngIf="sys.siteId" class="opacity-75">site {{ sys.siteId }}</span>
            <app-integration-pill *ngIf="s.igator.systems.length > 1" [status]="sys.pull.status"></app-integration-pill>
          </span>
          <span class="text-xs opacity-75">
            Last pull {{ sys.pull.lastAttemptAt ? (sys.pull.lastAttemptAt | drawerTime) : 'none recorded' }}<ng-container *ngIf="sys.pull.lastAttemptFailed"> (failed)</ng-container>
            · newest run {{ sys.newestRunAt ? (sys.newestRunAt | drawerTime) : 'none' }}
          </span>
        </dd>
      </ng-container>
    </dl>
    <p *ngIf="crossTenantTaps() as note" data-warning="igator" class="m-0 rounded-md bg-[var(--notification-warning)]/15 px-3 py-2">{{ note }}</p>
    <ng-container *ngTemplateOutlet="editAction; context: { tab: 'igator', label: taps().length ? 'Edit iGator taps' : 'Link iGator taps' }"></ng-container>
  </section>

  <!-- Weather: no edit action, the link editor has no weather tab. -->
  <section data-card="weather" class="flex flex-col gap-3 rounded-lg border border-[var(--app-border)] p-4">
    <div class="flex items-center justify-between gap-3">
      <div role="heading" aria-level="3" class="flex items-center gap-2 text-[15px] font-semibold text-[var(--primary-dark)]">
        <span class="material-icons text-[20px]!" aria-hidden="true">cloud</span>Weather (Pessl)
      </div>
      <app-integration-pill [status]="s.weather.status" [help]="true" [weather]="true" [km]="s.weather.checkKm"></app-integration-pill>
    </div>
    <p class="m-0 leading-normal" [ngClass]="reasonClasses(s.weather.status)">{{ s.weather.reason | drawerTime:'text' }}</p>
    <dl class="m-0 grid grid-cols-[112px_minmax(0,1fr)] gap-x-3 gap-y-2">
      <dt class="opacity-75">Station</dt>
      <dd class="m-0 break-words">{{ stationLine() }}</dd>
      <ng-container *ngIf="distanceLine() as distance">
        <dt class="opacity-75">Distance</dt>
        <dd class="m-0">{{ distance }}</dd>
      </ng-container>
      <ng-container *ngFor="let sensor of s.weather.sensors">
        <dt class="opacity-75">{{ sensor.sensor }}</dt>
        <dd class="m-0 flex flex-wrap items-center gap-2">
          {{ sensor.newestReadingAt ? (sensor.newestReadingAt | drawerTime) : 'No readings' }}
          <app-integration-pill *ngIf="sensor.status !== 'UP'" [status]="sensor.status"></app-integration-pill>
        </dd>
      </ng-container>
    </dl>
  </section>
</div>

<!-- Off, with the reason beside it, until the field context has loaded for this field. -->
<ng-template #editAction let-tab="tab" let-label="label">
  <div class="flex flex-wrap items-center gap-x-3">
    <button type="button" [attr.data-edit]="tab" (click)="editLink(tab)"
            [attr.aria-disabled]="editBlocked() ? 'true' : null"
            [attr.aria-describedby]="editBlocked() ? 'integration-edit-blocked-' + tab : null"
            class="min-h-11 font-semibold text-[var(--primary-dark)] hover:underline aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:no-underline">{{ label }}</button>
    <span *ngIf="editBlocked() as reason" [id]="'integration-edit-blocked-' + tab" class="text-xs opacity-75">{{ reason }}</span>
  </div>
</ng-template>
